"use client";

import React from "react";
import { motion } from "framer-motion";
import { SectionReveal } from "./SectionReveal";
import {
  CarFront,
  MapPin,
  Car,
  CreditCard,
} from "lucide-react";

const STEPS = [
  {
    id: 1,
    number: "01",
    label: "Choose Your Service",
    description:
      "Select Self Drive, Self Ride, Chauffeur or Rider and choose One Way or Round Trip where applicable.",
    icon: CarFront,
    bgColor: "bg-[#FFB51D]/20 dark:bg-[#FFB51D]/30",
    borderColor: "border-[#D4A017]/70 dark:border-[#FFB51D]/70",
    iconColor: "text-[#D4A017] dark:text-[#FFB51D]",
    pathPosition: "peak" as const,
  },
  {
    id: 2,
    number: "02",
    label: "Plan Your Journey",
    description:
      "Select your pickup/drop location, travel date & time and add sightseeing locations for eligible Round Trip bookings.",
    icon: MapPin,
    bgColor: "bg-[#FFB51D]/20 dark:bg-[#FFB51D]/30",
    borderColor: "border-[#D4A017]/70 dark:border-[#FFB51D]/70",
    iconColor: "text-[#D4A017] dark:text-[#FFB51D]",
    pathPosition: "trough" as const,
  },
  {
    id: 3,
    number: "03",
    label: "Choose Your Vehicle",
    description:
      "View available vehicles, package details, included KM/hours and applicable charges, then select your preferred vehicle.",
    icon: Car,
    bgColor: "bg-[#FFB51D]/20 dark:bg-[#FFB51D]/30",
    borderColor: "border-[#D4A017]/70 dark:border-[#FFB51D]/70",
    iconColor: "text-[#D4A017] dark:text-[#FFB51D]",
    pathPosition: "peak" as const,
  },
  {
    id: 4,
    number: "04",
    label: "Confirm & Pay",
    description:
      "Review your booking summary, accept the applicable Terms & Conditions, make the payment and receive your booking confirmation.",
    icon: CreditCard,
    bgColor: "bg-[#FFB51D]/20 dark:bg-[#FFB51D]/30",
    borderColor: "border-[#D4A017]/70 dark:border-[#FFB51D]/70",
    iconColor: "text-[#D4A017] dark:text-[#FFB51D]",
    pathPosition: "trough" as const,
  },
];

const SUBTITLE_TEXT = "Simple. Seamless. Ready for the Road.";
const INTRO_TEXT =
  "Choose your service, select your vehicle, complete your booking and start your journey with I K TOURS AND TRAVELS.";

export function IKHowItWorks() {
  return (
    <SectionReveal
      className="relative py-8 sm:py-10 md:py-12 lg:py-16"
      aria-labelledby="how-it-works-heading"
    >
      <div className="container mx-auto px-4 md:px-6">
        {/* Header & Subheading */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.4, ease: [0.22, 0.61, 0.36, 1] }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <h2
            id="how-it-works-heading"
            className="text-3xl md:text-4xl font-bold text-[#131222] dark:text-white mb-2"
          >
            How It Works
          </h2>
          <p className="text-base sm:text-lg font-semibold text-[#D4A017] dark:text-[#FFB51D] mb-3">
            {SUBTITLE_TEXT}
          </p>
          <p className="text-sm md:text-base text-[#131222]/80 dark:text-neutral-300 leading-relaxed">
            {INTRO_TEXT}
          </p>
        </motion.div>

        {/* Four-step process - path aligned with step centers */}
        <div className="relative min-h-[200px]">
          
          <div
            className="hidden lg:block absolute left-0 right-0 top-1/2 -translate-y-1/2 h-32 pointer-events-none z-0 text-neutral-500 dark:text-neutral-400"
            aria-hidden
          >
            <svg
              className="w-full h-full overflow-visible"
              viewBox="0 0 1000 100"
              fill="none"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Wavy path: start → peak1 → trough1 → peak2 → trough2 → final peak */}
              <path
                d="M 0 50 Q 125 15, 250 50 Q 375 85, 500 50 Q 625 15, 750 50 Q 875 85, 975 50 L 975 20"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="8 6"
              />
              {/* End dot at final peak with safe padding */}
              <circle
                cx="975"
                cy="20"
                r="5"
                fill="#131222"
                className="dark:fill-white"
              />
            </svg>
          </div>

          {/* Steps - number/title above icon, description below */}
          <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {STEPS.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 1.2,
                    ease: [0.22, 0.61, 0.36, 1],
                    delay: index * 0.1,
                  }}
                  className="relative flex flex-col items-center min-w-0"
                >
                  {/* Number + Title */}
                  <div className="flex items-center gap-2 justify-center flex-wrap mb-3 min-h-[44px]">
                    <span className="text-3xl md:text-4xl font-bold text-[#131222] dark:text-white shrink-0">
                      {step.number}
                    </span>
                    <h3 className="text-sm md:text-base font-semibold text-[#131222] dark:text-white text-center">
                      {step.label}
                    </h3>
                  </div>

                  {/* Icon - all steps use same circular style */}
                  <div
                    className={`relative z-10 flex items-center justify-center rounded-full border-2 h-16 w-16 shrink-0 ${step.bgColor} ${step.borderColor} ${step.iconColor}`}
                    aria-hidden
                  >
                    <IconComponent
                      className="w-8 h-8 shrink-0"
                      strokeWidth={2.5}
                    />
                  </div>

                  {/* Description */}
                  <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 text-center mt-3.5 leading-relaxed max-w-[280px]">
                    {step.description}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile - visible dashed connecting line */}
          <div
            className="md:hidden flex justify-center items-center gap-3 mt-8 px-4"
            aria-hidden
          >
            <div className="h-[2px] flex-1 max-w-16 bg-neutral-500 dark:bg-neutral-400 rounded-full opacity-80" />
            <div className="h-[2px] flex-1 max-w-16 bg-neutral-500 dark:bg-neutral-400 rounded-full opacity-80" />
            <div className="h-[2px] flex-1 max-w-16 bg-neutral-500 dark:bg-neutral-400 rounded-full opacity-80" />
          </div>
        </div>
      </div>
    </SectionReveal>
  );
}
